import { CrosstabDataCell, CrosstabSimpleDataCell, CrosstabTableRow } from "types/crosstab/crosstab-results-types";
import {
  CrosstabDefnCol,
  CrosstabDefnItemMultiScore,
  CrosstabDefnItemQuestion,
  CrosstabDefnRow,
  CrosstabMultiScoreComparison,
  QuestionCompareCrosstabResponse,
} from "types";

import { range } from "@civicscience/chops";

type ProcessQuestionComparisonRow = {
  colDefns: CrosstabDefnCol[];
  rowDefn: CrosstabDefnItemQuestion;
  rowData: QuestionCompareCrosstabResponse[];
};

type ProcessMultiScoreComparisonRow = {
  colDefns: CrosstabDefnCol[];
  rowDefn: CrosstabDefnItemMultiScore;
  rowData: CrosstabMultiScoreComparison[];
};

const getHeaderRowTitle = (rowDefn: CrosstabDefnRow): string => {
  if (rowDefn.label) {
    return rowDefn.label;
  }

  return rowDefn.itemType === "question" ? rowDefn.questionText : rowDefn.multiscoreName;
};

/**
 * Returns an array of column titles for a "header-row".
 * This takes into account the columns `itemType` as well
 * as any custom labels applied by the user.
 */
const getHeaderRowColumnTitles = (
  col: CrosstabDefnCol,
  data: QuestionCompareCrosstabResponse | CrosstabMultiScoreComparison,
): { title: string }[] => {
  if (col.itemType === "segment") {
    // segments only have a single column by definition
    return [{ title: col.segmentName || "" }];
  }

  if (col.answerGrouping) {
    return data.results.colSummary.map((x) => ({ title: x.label }));
  }

  return col.answerChoices.map((x) => ({ title: x.label || x.answerChoiceText }));
};

/**
 * Gets the title for a "data-row".
 * This takes into account answer groupings as well as custom labels.
 */
const getDataRowTitle = (
  row: CrosstabDefnItemQuestion,
  data: QuestionCompareCrosstabResponse,
  index: number,
): string => {
  if (row.answerGrouping) {
    return data.results.rowSummary[index].label;
  }

  return row.answerChoices[index].label || row.answerChoices[index].answerChoiceText;
};

/**
 * Processes a CrosstabComparisonRow to produce table rows.
 * The general format of table rows from a QuestionComparison is:
 *
 * - header-row
 * - data-row
 * - ...
 * - column-total-row
 * - significance-row
 */
export const processQuestionRowComparison = ({
  colDefns,
  rowDefn,
  rowData,
}: ProcessQuestionComparisonRow): CrosstabTableRow[] => {
  return [
    {
      kind: "header-row",
      title: getHeaderRowTitle(rowDefn),
      filters: {
        segmentName: rowDefn.segmentName ?? null,
      },
      headerGroups: rowData.map((x, i) => ({
        headers: getHeaderRowColumnTitles(colDefns[i], x),
        supportsRowTotal: x.results.colSummary.length > 1,
      })),
    },
    ...range(rowData[0].results.table.length).map((resultRowIndex): CrosstabTableRow => ({
      kind: "data-row",
      title: getDataRowTitle(rowDefn, rowData[0], resultRowIndex),
      dataCellGroups: rowData.map((d) => ({
        totalCell:
          d.results.colSummary.length > 1
            ? {
                value: d.results.rowSummary[resultRowIndex].count,
                percent: d.results.rowSummary[resultRowIndex].pct,
              }
            : undefined,
        dataCells: d.results.table[resultRowIndex].cols.map(({ dpct, ...cell }): CrosstabDataCell => ({
          ...cell,
          diffInPercentage: dpct,
        })),
      })),
    })),
    {
      kind: "col-total-row",
      dataCellGroups: rowData.map((d) => ({
        totalCell: d.results.colSummary.length > 1 ? { value: d.stats.total } : undefined,
        dataCells: d.results.colSummary.map((col) => ({ value: col.count, percent: col.pct })),
      })),
    },
    {
      kind: "significance-row",
      significanceGroups: rowData.map((x) => ({
        colCount: x.results.colSummary.length,
        supportsRowTotal: x.results.colSummary.length > 1,
        stats: x.stats,
      })),
    },
  ];
};

export const processMultiScoreRowComparison = ({
  colDefns,
  rowDefn,
  rowData,
}: ProcessMultiScoreComparisonRow): CrosstabTableRow[] => {
  return [
    {
      kind: "header-row",
      title: getHeaderRowTitle(rowDefn),
      filters: {},
      headerGroups: rowData.map((x, i) => ({
        headers: getHeaderRowColumnTitles(colDefns[i], x),
        supportsRowTotal: x.results.colSummary.length > 1,
      })),
    },
    {
      kind: "simple-data-row",
      dataCellGroups: rowData.map((d) => ({
        supportsRowTotal: d.results.colSummary.length > 1,
        dataCells: d.results.table.cols.map(({ score }): CrosstabSimpleDataCell => ({ count: score })),
      })),
    },
  ];
};
